import React, { useEffect, useMemo, useState } from 'react';
import Modal from '../common/Modal';
import ProfitabilityLineChart from '../charts/ProfitabilityLineChart';
import ProfitabilityMonthsTable from '../charts/ProfitabilityMonthsTable';
import { IconArrowRight } from '../common/Icons';
import {
  MONTHS,
  getMonthRange,
  filterExpensesByRange,
  filterServicesByRange,
  computeProfitabilityTotals
} from '../../utils/profitability';
import './ModalForms.css';
import './ProfitabilityHistoryModal.css';

const MIN_YEAR = 2025;

const ProfitabilityHistoryModal = ({
  isOpen, onClose, expenses, services, convertPesosToUsd, initialYear
}) => {
  const now = useMemo(() => new Date(), []);
  const [year, setYear] = useState(Math.max(initialYear || now.getFullYear(), MIN_YEAR));

  useEffect(() => {
    if (isOpen) setYear(Math.max(initialYear || now.getFullYear(), MIN_YEAR));
  }, [isOpen, initialYear, now]);

  const monthlyData = useMemo(() => MONTHS.map((m) => {
    const range = getMonthRange(year, m.value);
    const monthExpenses = filterExpensesByRange(expenses, range.from, range.to);
    const monthServices = filterServicesByRange(services, range.from, range.to);
    const totals = computeProfitabilityTotals(monthExpenses, monthServices, convertPesosToUsd);
    const values = {};
    totals.net.forEach((row) => { values[row.currency] = row.total; });
    return { month: m.value, values };
  }), [expenses, services, convertPesosToUsd, year]);

  const currencies = useMemo(() => {
    const set = new Set();
    monthlyData.forEach((row) => Object.keys(row.values).forEach((c) => set.add(c)));
    if (set.size === 0) set.add('$');
    return [...set].sort((a, b) => (a === '$' ? -1 : b === '$' ? 1 : a.localeCompare(b)));
  }, [monthlyData]);

  return (
    <Modal
      title="Histórico de rentabilidad"
      isOpen={isOpen}
      onClose={onClose}
      contentStyle={{ maxWidth: 980, width: '100%' }}
      contentClassName="profitability-history-modal"
    >
      <div className="modal-form">
        <div className="year-nav">
          <button
            type="button"
            className="icon-btn year-nav-btn"
            onClick={() => setYear((y) => Math.max(y - 1, MIN_YEAR))}
            disabled={year <= MIN_YEAR}
            aria-label="Año anterior"
            title="Año anterior"
          >
            <IconArrowRight style={{ transform: 'rotate(180deg)' }} />
          </button>
          <span className="year-nav-value">{year}</span>
          <button
            type="button"
            className="icon-btn year-nav-btn"
            onClick={() => setYear((y) => y + 1)}
            aria-label="Año siguiente"
            title="Año siguiente"
          >
            <IconArrowRight />
          </button>
        </div>

        <div className="history-content">
          <div className="history-chart">
            <ProfitabilityLineChart months={MONTHS} currencies={currencies} data={monthlyData} year={year} />
          </div>
          <div className="history-table">
            <ProfitabilityMonthsTable months={MONTHS} currencies={currencies} data={monthlyData} />
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ProfitabilityHistoryModal;
